import mixpanel from 'mixpanel-browser'

export type MixpanelVersionT = `v${number}@${number}-${number}-${number}` // v1.0@YYYY-MM-DD

/**
 * 로그인 여부와 무관하게 init 직후 등록되는 클라이언트 식별 속성.
 * 유저 속성이 아니므로 로그인 후 전역 속성 스키마가 아니라 여기에 둔다 —
 * 그래야 로그인 전 이벤트에도 붙어 표본에 구멍이 생기지 않는다.
 */
export type ClientProperties = {
  deviceType: string
  releaseId: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class MixpanelClient<EventSchemaT extends Record<string, any>> {
  private static _instance = mixpanel

  version?: {
    globalPropertiesVersion: MixpanelVersionT
    userPropertiesVersion: MixpanelVersionT
  }
  // 초기화(requestIdleCallback 지연)보다 먼저 도착한 유저 상태 갱신 호출을 담아두었다가
  // initialize 직후 순서대로 재생한다. 지연 init 과 tracking effect 의 실행 순서가 비결정적이라,
  // 큐잉 없이 !initialized 로 early-return 하면 그 세션 동안 프로필 갱신이 영구 유실된다.
  private pendingOperations: Array<() => void> = []
  constructor(
    private readonly projectToken?: string,
    private config?: Parameters<typeof mixpanel.init>[1],
    version?: MixpanelClient<EventSchemaT>['version'],
  ) {
    this.version = version
  }

  get initialized() {
    return '__loaded' in MixpanelClient._instance && MixpanelClient._instance.__loaded
  }

  initialize({ deviceType, releaseId }: ClientProperties) {
    if (!this.projectToken) return
    MixpanelClient._instance.init(this.projectToken, this.config)
    MixpanelClient._instance.register({
      $device_type: deviceType,
      $user_agent: navigator.userAgent,
      releaseId,
    })
    this.flushPendingOperations()
  }

  private runWhenInitialized(operation: () => void) {
    if (this.initialized) {
      operation()
      return
    }
    // 토큰이 없으면 initialize 가 조기 반환해 flush 시점이 영영 오지 않으므로 큐잉하지 않는다
    if (!this.projectToken) return
    this.pendingOperations.push(operation)
  }

  private flushPendingOperations() {
    const operations = this.pendingOperations
    this.pendingOperations = []
    // requestIdleCallback 안에서 실행되므로 한 operation 의 예외가 나머지 flush 를 건너뛰거나
    // initialize 밖으로 전파되지 않도록 각각 격리한다
    operations.forEach((operation) => {
      try {
        operation()
      } catch (error) {
        console.error('Mixpanel pending operation 실행 실패:', error)
      }
    })
  }

  static getMixpanel() {
    return MixpanelClient._instance
  }

  /**
   * @see https://github.com/mixpanel/docs/blob/main/reference/Ingestion%20API/events/track-event.md#limits
   * @description
   * Must be smaller than 1MB of uncompressed JSON.
   * Must have fewer than 255 properties.
   * All nested object properties must have fewer than 255 keys and max nesting depth is 3.
   * All array properties must have fewer than 255 elements.
   */
  track<K extends Extract<keyof EventSchemaT, string>>(
    eventName: Parameters<typeof MixpanelClient._instance.track>[0],
    properties?: EventSchemaT[K] extends never ? never : EventSchemaT[K],
    optionsOrCallback?: Parameters<typeof MixpanelClient._instance.track>[2],
    callback?: Parameters<typeof MixpanelClient._instance.track>[3],
  ) {
    if (!this.initialized) return
    // properties가 있는 경우 제한사항 검증
    if (properties) {
      const validation = MixpanelValidator.validateMixpanelLimits(properties)

      if (!validation.isValid) {
        if (process.env.NODE_ENV === 'development' && this.config?.debug) {
          console.error('Mixpanel 제한사항 위반:', {
            eventName,
            errors: validation.errors,
          })
        }

        // 제한사항을 위반한 경우 이벤트 전송을 중단하거나 제한된 데이터만 전송
        return
      }
    }

    MixpanelClient._instance.track(eventName, properties, optionsOrCallback, callback)
  }

  identify(...args: Parameters<typeof MixpanelClient._instance.identify>) {
    this.runWhenInitialized(() => MixpanelClient._instance.identify(...args))
  }

  setGlobalEventProperty(...args: Parameters<typeof MixpanelClient._instance.register>) {
    // 큐잉되는 경우 flush 시점까지 호출자가 원본 객체를 변경할 수 있으므로 호출 시점에 payload 를 고정한다
    const [firstArg, ...rest] = args
    const props = { ...firstArg, globalPropertiesVersion: this.version?.globalPropertiesVersion }

    this.runWhenInitialized(() => MixpanelClient._instance.register(props, ...rest))
  }

  setUserProperty(...args: Parameters<typeof MixpanelClient._instance.people.set>) {
    // 큐잉되는 경우 flush 시점까지 호출자가 원본 객체를 변경할 수 있으므로 호출 시점에 payload 를 고정한다
    const [firstArg, ...rest] = args
    const props = { ...firstArg, userPropertiesVersion: this.version?.userPropertiesVersion }

    this.runWhenInitialized(() => MixpanelClient._instance.people.set(props, ...rest))
  }

  unregister(...args: Parameters<typeof MixpanelClient._instance.unregister>) {
    if (!this.initialized) return
    MixpanelClient._instance.unregister(...args)
  }

  removeUserProperty(...args: Parameters<typeof MixpanelClient._instance.people.unset>) {
    if (!this.initialized) return
    MixpanelClient._instance.people.unset(...args)
  }

  getEventProperty(key: string) {
    if (!this.initialized) return
    return MixpanelClient._instance.get_property(key)
  }

  /**
   * 트래킹 opt-in (이벤트 전송 활성화).
   * `opt_out_tracking_by_default: true` 로 초기화한 경우 명시적으로 호출해야 이벤트가 전송된다.
   */
  optInTracking(...args: Parameters<typeof MixpanelClient._instance.opt_in_tracking>) {
    if (!this.initialized) return
    MixpanelClient._instance.opt_in_tracking(...args)
  }

  /**
   * 트래킹 opt-out (이벤트 전송 차단).
   * 호출 이후 track / identify / register / people.set 등은 모두 무시된다.
   */
  optOutTracking(...args: Parameters<typeof MixpanelClient._instance.opt_out_tracking>) {
    if (!this.initialized) return
    MixpanelClient._instance.opt_out_tracking(...args)
  }
}

// 검증 결과 타입
interface ValidationResult {
  isValid: boolean
  errors: string[]
}

// Mixpanel 제한사항 검증 유틸리티 클래스
class MixpanelValidator {
  // Mixpanel 제한사항 상수
  static MIXPANEL_LIMITS = {
    MAX_JSON_SIZE_BYTES: 1024 * 1024, // 1MB
    MAX_PROPERTIES: 255,
    MAX_NESTING_DEPTH: 3,
    MAX_ARRAY_ELEMENTS: 255,
    MAX_OBJECT_KEYS: 255,
  } as const
  /**
   * JSON 크기 검증 (1MB 미만)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static validateJsonSize(data: any): ValidationResult {
    try {
      const jsonString = JSON.stringify(data)
      const sizeInBytes = new Blob([jsonString]).size

      if (sizeInBytes >= MixpanelValidator.MIXPANEL_LIMITS.MAX_JSON_SIZE_BYTES) {
        return {
          isValid: false,
          errors: [
            `JSON 크기가 제한을 초과했습니다. 현재: ${sizeInBytes} bytes, 제한: ${MixpanelValidator.MIXPANEL_LIMITS.MAX_JSON_SIZE_BYTES} bytes`,
          ],
        }
      }

      return { isValid: true, errors: [] }
    } catch (error) {
      return {
        isValid: false,
        errors: [`JSON 직렬화 중 오류 발생: ${error}`],
      }
    }
  }

  /**
   * 객체의 프로퍼티 개수 검증 (255개 미만)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static validatePropertyCount(obj: Record<string, any>): ValidationResult {
    const propertyCount = Object.keys(obj).length

    if (propertyCount >= MixpanelValidator.MIXPANEL_LIMITS.MAX_PROPERTIES) {
      return {
        isValid: false,
        errors: [
          `프로퍼티 개수가 제한을 초과했습니다. 현재: ${propertyCount}, 제한: ${MixpanelValidator.MIXPANEL_LIMITS.MAX_PROPERTIES}`,
        ],
      }
    }

    return { isValid: true, errors: [] }
  }

  /**
   * 중첩 깊이 및 배열 요소 개수 검증
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static validateNestingAndArrays(obj: any, currentDepth = 0): ValidationResult {
    const errors: string[] = []

    // 중첩 깊이 검증
    if (currentDepth > MixpanelValidator.MIXPANEL_LIMITS.MAX_NESTING_DEPTH) {
      errors.push(
        `중첩 깊이가 제한을 초과했습니다. 현재: ${currentDepth}, 제한: ${MixpanelValidator.MIXPANEL_LIMITS.MAX_NESTING_DEPTH}`,
      )
    }

    if (obj === null || typeof obj !== 'object') {
      return { isValid: errors.length === 0, errors }
    }

    // 배열 요소 개수 검증
    if (Array.isArray(obj)) {
      if (obj.length >= MixpanelValidator.MIXPANEL_LIMITS.MAX_ARRAY_ELEMENTS) {
        errors.push(
          `배열 요소 개수가 제한을 초과했습니다. 현재: ${obj.length}, 제한: ${MixpanelValidator.MIXPANEL_LIMITS.MAX_ARRAY_ELEMENTS}`,
        )
      }

      // 배열의 각 요소에 대해 재귀 검증
      for (const item of obj) {
        const result = this.validateNestingAndArrays(item, currentDepth + 1)
        errors.push(...result.errors)
      }
    } else {
      // 객체 키 개수 검증
      const keyCount = Object.keys(obj).length
      if (keyCount >= MixpanelValidator.MIXPANEL_LIMITS.MAX_OBJECT_KEYS) {
        errors.push(
          `객체 키 개수가 제한을 초과했습니다. 현재: ${keyCount}, 제한: ${MixpanelValidator.MIXPANEL_LIMITS.MAX_OBJECT_KEYS}`,
        )
      }

      // 객체의 각 값에 대해 재귀 검증
      for (const value of Object.values(obj)) {
        const result = this.validateNestingAndArrays(value, currentDepth + 1)
        errors.push(...result.errors)
      }
    }

    return { isValid: errors.length === 0, errors }
  }

  /**
   * 모든 Mixpanel 제한사항을 한번에 검증
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static validateMixpanelLimits(data: any): ValidationResult {
    const allErrors: string[] = []

    // JSON 크기 검증
    const sizeResult = this.validateJsonSize(data)
    allErrors.push(...sizeResult.errors)

    // 프로퍼티 개수 검증 (최상위 객체인 경우)
    if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
      const propertyResult = this.validatePropertyCount(data)
      allErrors.push(...propertyResult.errors)
    }

    // 중첩 및 배열 검증
    const nestingResult = this.validateNestingAndArrays(data)
    allErrors.push(...nestingResult.errors)

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
    }
  }
}
